import { Component, OnInit, Injector, OnDestroy, ViewChild } from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import DataGrid from 'devextreme/ui/data_grid';
import CustomStore from 'devextreme/data/custom_store';

// components
import { BuyerComponent } from './buyer.component';
import { FullListComponent } from '../../shared/components/full-list/full-list.component';

// models
import { Auction } from '../../shared/models/auction';
import { AuctionCluster } from '../../shared/models/auction-cluster';
import { Clock } from '../../shared/models/clock';
import { Currency } from '../../shared/models/currency';

import { AuctionClusterBuyer } from '../shared/models/auction-cluster-buyer';

// services
import { ClockService } from '../../shared/services/clock.service';
import { CurrencyService } from '../../shared/services/currency.service';
import { LanguageService } from '../../shared/services/language.service';

import { AuctionClusterAuctionService } from '../shared/services/auction-cluster-auction.service';
import { AuctionClusterBuyerService } from '../shared/services/auction-cluster-buyer.service';
import { AuctionClusterService } from '../shared/services/auction-cluster.service';
import { MasterDataService } from '../shared/services/master-data.service';


@Component({
  selector: 'buyers-component',
  templateUrl: 'buyers.component.html',
  styleUrls: ['./buyers.component.scss']
})
export class BuyersComponent extends FullListComponent<AuctionClusterBuyer, BuyerComponent> implements OnInit, OnDestroy {

  @ViewChild('confirmationDelete') confirmationDelete: any;
  @ViewChild('confirmationDeactivate') confirmationDeactivate: any;
  @ViewChild('details') detailsComponent: BuyerComponent;

  auctions: Array<Auction> = [];
  clocks: Array<Clock> = [];
  currencies: Array<Currency> = [];
  buyerToDisable: AuctionClusterBuyer;
  customDataSource: CustomStore;
  dataGridInstance: DataGrid;
  allBuyers: Array<AuctionClusterBuyer> = [];
  auctionCluster: AuctionCluster;
  secondRestrictionMasterDataList: any;
  secondRestrictionMasterDataName: string;
  masterDataListValues: any = [];

  rtlEnabled = localStorage.getItem('last-selected-language-direction') ? JSON.parse(localStorage.getItem('last-selected-language-direction')) : false;
  private _subscription: Subscription;

  constructor(
    protected injector: Injector,
    private dataService: AuctionClusterBuyerService,
    private auctionService: AuctionClusterAuctionService,
    private clockService: ClockService,
    private currrencyService: CurrencyService,
    private languageService: LanguageService,
    private auctionClusterService: AuctionClusterService,
    private masterDataService: MasterDataService
  ) {
    super(injector, AuctionClusterBuyer);
    this._subscription = this.languageService.direction.subscribe(dir => {
      this.rtlEnabled = dir;
    });

    this.customDataSource = new CustomStore({
      key: 'buyerId',
      load: (loadOptions) => {

        const observables = [];
        observables.push(this.dataService.getBuyersPaged(this.id, loadOptions));
          

        return new Promise((resolve, reject) => {
          forkJoin(observables).subscribe(result => {
            this.items = result[0]['data'];

            this.getAllBuyers();
            this.buildClocksString();

            resolve({
              data: result[0]['data'],
              totalCount: result[0]['totalCount']
            });
          }, error => {
            reject(error);
          });
        });
      }
    });
  }

  ngOnInit() {
    this.title.set('SHARED.BUYER_MANAGEMENT');
    this.setTranslations("BUYER");
    this.getAllBuyers();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._subscription.unsubscribe();
  }

  initColumn = (e: any) => {
    this.dataGridInstance = e.component;
  }

  // tslint:disable:no-magic-numbers
  getData() {
    this.spinner.show();

    forkJoin(
      //this.dataService.getBuyers(this.id),
      this.auctionService.getAuctions(this.id),
      this.clockService.getAllClusterClocks(this.id),
      this.currrencyService.getCurrencies(),
      this.auctionClusterService.getAuctionCluster(this.id),
    ).subscribe(result => {
      this.auctions = result[0];
      this.clocks = result[1];
      this.currencies = result[2];
      this.auctionCluster = result[3];

      this.secondRestrictionMasterDataList = this.auctionCluster.masterDataList.find(_ => _.masterDataListId === this.auctionCluster.secondRestrictionMasterDataId);
      if (this.secondRestrictionMasterDataList) {
        this.secondRestrictionMasterDataName = this.languageService.getTranslatableText(this.secondRestrictionMasterDataList.name)
        let masterDataListId = this.secondRestrictionMasterDataList.masterDataListId;
        
        this.masterDataService.getMasterDataListValues(masterDataListId, this.id)
          .subscribe(res => {
            this.masterDataListValues = res;
          },
            error => {
              this.errorService.show(error);
              this.spinner.hide();
            });
      }
      
      this.spinner.hide();
    }, error => {
      this.errorService.show(error);
      this.spinner.hide();
    });
  }
  // tslint:enable:no-magic-numbers

  getAllBuyers() {
    this.spinner.show();

    this.dataService.getBuyers(this.id)
      .subscribe(result => {
        this.allBuyers = result;

        this.spinner.hide();
      }, error => {
        this.errorService.show(error);
        this.spinner.hide();
      });
  }

  edit = (e: any) => {
    const buyerId = e.row !== undefined ? e.row.data.buyerId : e.data.buyerId;
    this.detailsComponent.modalTitle = this.translations.EDIT;
    this.detailsComponent.open(this.allBuyers, buyerId, this.auctions, this.clocks, this.id, this.currencies, this.secondRestrictionMasterDataName, this.masterDataListValues);
  }

  add() {
    this.detailsComponent.modalTitle = this.translations.ADD_NEW;
    this.detailsComponent.open(this.allBuyers, null, this.auctions, this.clocks, this.id, this.currencies, this.secondRestrictionMasterDataName, this.masterDataListValues);
  }

  onDetailsComponentClosed(isDataChanged: boolean) {
    if (isDataChanged) {
      this.dataGridInstance.getDataSource().reload();
    }
  }

  buildClocksString() {
    this.items.forEach(buyer => {
      const clockNames: Array<string> = [];
      if (buyer.accessToAllAuctionsAndClocks) {
        if (this.translations) {
          buyer.clocks = this.translations.ALL_CLOCKS;
        }
      }
      else {
        buyer.buyerAuctionClocks.forEach(auctionClock => {
          const auction = this.auctions.find(a => a.auctionId == auctionClock.auctionId);

          const clock = this.clocks.find(f => f.clockId === auctionClock.clockId);
          if (clock) {
            clockNames.push(auction.name + ' (' + clock.name + ')');
          }
          else {
            if (this.translations) {
              clockNames.push(auction.name + ' (' + this.translations.ALL_CLOCKS + ')');
            }
          }
        });
        buyer.clocks = clockNames.join(', ');
      }
    });
  }

  activateBuyer = (e: any) => {    
    const buyer: AuctionClusterBuyer = e.row.data;
    this.buyerToDisable = buyer;

    this.enableDisableBuyer();
  }

  deactivateBuyer = (e: any) => {
    const buyer: AuctionClusterBuyer = e.row.data;
    this.buyerToDisable = buyer;

    this.confirmationDeactivate.opened = true; 
  }

  enableDisableBuyer = () => {
    this.spinner.show();
    this.dataService.enableDisable(this.id, this.buyerToDisable).subscribe(() => {
      this.dataGridInstance.getDataSource().reload();
    },
      error => {
        this.errorService.show(error);
      }
    );
    
    this.spinner.hide();
    this.confirmationDeactivate.opened = false; 
  }

  calculateDisabledDisplayValue = (e: any) => e.row.data.isActiveOnAuctionCluster;
  calculateEnabledDisplayValue = (e: any) => !e.row.data.isActiveOnAuctionCluster;

  deleteItem = (e: any) => {
    this.itemIdToDelete = e.row.data.buyerId;
    this.confirmationDelete.opened = true;
  }

  deleteSelected() {
    this.spinner.show();
    this.dataService.delete(this.id, this.itemIdToDelete)
      .subscribe(res => {
        this.dataGridInstance.getDataSource().reload();
        this.spinner.hide();
      },
      error => {
        if (error.error === 'BUYER.BUYER_HAS_TRANSACTIONS') {
          error = this.translate.instant('BUYER.BUYER_HAS_TRANSACTIONS');
        }
        this.errorService.show(error);
        this.spinner.hide();
      });

    this.confirmationDelete.opened = false;
  }
}
